(ns quuppa.components
  (:require [reagent.core :as r]
            [komponentit.timeago :as timeago]
            [shadow.lazy :as lazy]
            [quuppa.date-time.core :as date-time]
            [quuppa.ui.icons :refer [icon]]
            [quuppa.ui.tooltip :as tooltip]
            [quuppa.ui.util :as util])
  (:require-macros [quuppa.common.version :refer [get-version-info]]))

(defn error-tree' [v]
  (if (map? v)
    [:ul (for [[k v] v]
           [:li {:key k} k ": " (error-tree' v)])]
    [:span (pr-str v)]))

(defn alert-danger
  "https://getbootstrap.com/docs/5.2/components/alerts/"
  {:arglists '([& children]
               [props & children])}
  [& args]
  (let [[props children] (util/parse-args args)]
    (into [:div.alert.alert-danger
           (merge {:role "alert"} props)]
          children)))

(defn error-alert [{:keys [error] :as err}]
  (when error
    [alert-danger
     [:h4 error]
     (when-let [description (:err-msg err)]
       [:p description])
     (when-let [data (:ex-data err)]
       [error-tree' (if-let [malli-error (:malli-error data)]
                      malli-error
                      data)])]))

(defn error-msg
  "Simplified version of 'error-alert', where :ex-data is not displayed to end user."
  [{:keys [error] :as err}]
  (if error
    [alert-danger
     [:h4 error]
     (when-let [description (:err-msg err)]
       [:p description])]
    [:div]))

(defn error-boundary [& _children]
  (let [error (r/atom nil)
        stack (r/atom nil)]
    (r/create-class
      {:displayName "ErrorBoundary"
       :component-did-catch (fn [_this _e info]
                              (reset! stack (.-componentStack info)))
       :get-derived-state-from-error (fn [e]
                                       (reset! error e)
                                       #js {})
       :reagent-render (fn [& children]
                         (if @error
                           [:div
                            {:style {:padding "1rem"
                                     :border "2px dashed darkred"}}
                            [:h2 "Unhandled exception during render call."]

                            [:h4 [:pre (str @error)]]

                            [:h5 "Stacktrace"]
                            [:pre (.-stack @error)]
                            [:h5 "Component stack"]
                            [:pre @stack]

                            (let [{:keys [app qpe-info-ui cloud-common]} (get-version-info)]
                              [:dl
                               [:dt "App version"]
                               [:dd (:version app)]

                               [:dt "App build"]
                               [:dd
                                (:build-time app)
                                [:span " (pipeline id: " (:pipeline-id app) ")"]]

                               [:dt "qpe-info-ui"]
                               [:dd
                                [:span " (sha: " (:ref-short qpe-info-ui) ")"]]

                               [:dt "cloud-common"]
                               [:dd
                                [:span " (sha: " (:ref-short cloud-common) ")"]]])

                            [:button
                             {:on-click #(reset! error nil)}
                             "Try again"]]
                           (into [:<>] children)))})))

(defn small-error-boundary
  "error-boundary is huge, this smaller box can be used with smaller components"
  [& _children]
  (let [error (r/atom nil)
        stack (r/atom nil)]
    (r/create-class
      {:displayName "FieldErrorBoundary"
       :component-did-catch (fn [_this _e info]
                              (reset! stack (.-componentStack info)))
       :get-derived-state-from-error (fn [e]
                                       (reset! error e)
                                       #js {})
       :reagent-render (fn [& children]
                         (if @error
                           [:div.bg-warning
                            {:style {:padding "0.5rem"
                                     :border "1px dashed darkred"}
                             :title (str "Stack:\n" (.-stack @error))}
                            [:strong "Unhandled exception during render call."]
                            [:h4 [:pre (str @error)]]
                            [:button
                             {:on-click #(reset! error nil)}
                             "Try again"]]
                           (into [:<>] children)))})))

(defn copy-text-input [{:keys [_value]} _copy-label]
  (let [input-ref (atom nil)
        input-ref-fn #(reset! input-ref %)]
    (fn [{:keys [value]} copy-label]
      [:div.input-group
       [:input.form-control
        {:value value
         :read-only true
         :ref input-ref-fn
         ;; Select input text when input is focused (single click)
         :on-focus (fn [e]
                     (let [el (.-target e)]
                       (.setSelectionRange el 0 (.. el -value -length))))}]
       [:div.input-group-append
        [:button.btn.btn-primary
         {:on-click (fn [_e]
                      (let [el @input-ref]
                        (.focus el)
                        (.setSelectionRange el 0 (.. el -value -length))
                        (.execCommand js/document "copy")))}
         copy-label]]])))

(defn lazy-component
  "React lazy component usable within react/Suspense.

  Provide shadow.lazy/loadable value to pointing to a Reagent component."
  [_opts loadable & _args]
  (let [ready? (r/atom (lazy/ready? loadable))]
    (fn [{:keys [fallback]} loadable & args]
      (if @ready?
        (into [@loadable] args)
        (do (-> (lazy/load loadable)
                (.then #(reset! ready? true)))
            fallback)))))

(defn info-button
  [title]
  [tooltip/overlay-trigger
   {:overlay (r/as-element [tooltip/tooltip title])
    :placement "bottom"}
   [:span
    [icon :info-circle-fill.text-gray-500
     {:title ""}]]])

;; Looks like we don't yet need debounced inputs
;; I.e. the on-change is triggerd for every change event, updating the state,
;; and triggering render.
(defn input-text
  "Match re-com input-text API with a very simple implementation.
  Probably not useful for new code."
  [{:keys [on-change attr value]}]
  [:input.form-control
   (assoc (merge {:type "text"}
                 attr)
          :value value
          :on-change (fn [e] (on-change (.. e -target -value) e)))])


(defn date-time
  ([instant] (date-time {} instant))
  ([props instant]
   (when instant
     [small-error-boundary
      [:span
       props
       (date-time/format-instant instant)
       " "
       [timeago/timeago-2
        {:value     (date-time/temporal->native-date instant)
         :format-fn (fn [val] (str "(" (date-time/duration-string val) ")"))}]]])))

(defn timeago [instant]
  [timeago/timeago-2
   {:value     (date-time/temporal->native-date instant)
    #_#_:format-fn (fn [{:keys [diff-ms] :as x}]
                 [:span
                  {:class (time->color-class diff-ms)}
                  (date-time/duration-string x)])}])
